import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { mapPatientSummaries } from '@/api/mappers';
import type { PatientSummaryViewModel } from '@/models/patient';

import { useDebouncedValue } from './useDebouncedValue';

/**
 * Minimum trimmed length before we fire a network request. Single-letter
 * searches match almost everything and waste the server's time; the UX
 * shows the empty-state copy until the user has typed a meaningful prefix.
 */
export const PATIENT_SEARCH_MIN_LENGTH = 2;
const PATIENT_SEARCH_DEBOUNCE_MS = 200;
/** Cap on quick-search results surfaced in the dialog. */
export const PATIENT_SEARCH_DEFAULT_LIMIT = 10;

export interface UsePatientSearchOptions {
  readonly limit?: number;
  /** Override the debounce window — tests pass `0` for determinism. */
  readonly debounceMs?: number;
}

export interface UsePatientSearchResult {
  /** Mapped patient summaries from the most recent settled query. */
  readonly results: readonly PatientSummaryViewModel[];
  /** True while the debounce is open OR a request is in flight. */
  readonly isLoading: boolean;
  /** Surfaces `ApiError` (or other network failure) when the fetch fails. */
  readonly error: Error | null;
}

/**
 * Live patient search hook. Debounces the caller-supplied query, gates
 * on a minimum length, and returns pre-mapped `PatientSummaryViewModel`s
 * so consumers (QuickSearch, future browse filters) never touch raw DTOs.
 *
 * The hook is intentionally "patient-only" even though `/search` returns
 * families too — QuickSearch's IA is patient-first for now. When a
 * family-results section lands we can add a `useFamilySearch` that shares
 * the same underlying request via React Query's dedup.
 */
export function usePatientSearch(
  rawQuery: string,
  options: UsePatientSearchOptions = {},
): UsePatientSearchResult {
  const limit = options.limit ?? PATIENT_SEARCH_DEFAULT_LIMIT;
  const debounceMs = options.debounceMs ?? PATIENT_SEARCH_DEBOUNCE_MS;

  const trimmed = rawQuery.trim();
  const debouncedQuery = useDebouncedValue(trimmed, debounceMs);
  const isActive = debouncedQuery.length >= PATIENT_SEARCH_MIN_LENGTH;

  const query = useQuery<readonly PatientSummaryViewModel[]>({
    queryKey: ["search", "patients", debouncedQuery, limit] as const,
    queryFn: async ({ signal }) => {
      const response = await apiClient.search(
        { q: debouncedQuery, limit },
        { signal },
      );
      return mapPatientSummaries(response.patients ?? []);
    },
    enabled: isActive,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  // "isLoading" covers two observable states: the debounce hasn't settled
  // (user is still typing) and the request itself is in flight. Either
  // one should surface a spinner in the UI. When the query is inactive
  // (below min length) we stay quiet so the empty-state copy can show.
  const debounceSettling = trimmed !== debouncedQuery;
  const isLoading = isActive && (debounceSettling || query.isFetching);

  // `placeholderData: keepPreviousData` keeps `query.data` populated across
  // query-key changes for smooth UX, but that also means it retains the
  // last active search's results when the user clears the input. Gate on
  // `isActive` so stale results never leak back into QuickSearch (or any
  // other consumer) once the query drops below the min length.
  return {
    results: isActive ? (query.data ?? []) : [],
    isLoading,
    error: isActive ? (query.error ?? null) : null,
  };
}
